

import { monoFontSx } from 'styles/tokens/typography';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Card,
  CardContent,
  Box,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Typography,
  FormControlLabel,
  Switch,
  Checkbox,
  Collapse,
  Chip,
  InputAdornment,
  Grid,
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import {
  DeleteOutline,
  ExpandMore,
  ExpandLess,
  AccessTime,
  PlayArrowOutlined,
  EditOutlined,
  EmailOutlined,
  AssignmentOutlined,
  Webhook,
  Add,
  Delete,
  Update,
  Visibility
} from '@mui/icons-material';
import MDButton from 'components/MDButton';
import { useAutomationContext } from '../context/AutomationContext';
import MetadataService from '../services/metadataService';
import { useAuth } from 'auth/Auth';
import DateField from './FieldSelector/DateField';
import {
  inputSx,
  selectSx,
  placeholderSx,
  badgeSx,
  mutedBadgeSx,
  statusColors,
  tintedIconButtonSx,
  dangerText,
  FONT
} from '../styles/wizardSx';

// Email and template previews render on a white canvas, like the email
// itself, with dark ink pinned so dark mode does not inherit near-white text.
const emailCanvasSx = (theme) => ({
  border: '1px solid',
  borderColor: theme.palette.outline.variant,
  borderRadius: 1,
  p: 2,
  bgcolor: theme.palette.common.white,
  color: theme.palette.getContrastText(theme.palette.common.white)
});

// Action types matching the reference implementation
const actionTypes = [
  { value: 'Create Task', label: 'Create Task', icon: <AssignmentOutlined /> },
  { value: 'Send Email', label: 'Send Email', icon: <EmailOutlined /> },
  { value: 'Update Field', label: 'Update Field', icon: <EditOutlined /> },
  { value: 'Create Record', label: 'Create Record', icon: <PlayArrowOutlined /> },
  { value: 'Update Related Record', label: 'Update Related Record', icon: <Update /> },
  { value: 'Webhook', label: 'Webhook', icon: <Webhook /> }
];

// variant="inspector": the automation canvas's step panel (layouts/crm/
// automation-canvas). Always open, no remove button or number badge (the
// canvas card has those), and no per-action delay (the canvas draws delays
// as Wait steps). The wizard uses the default card.
function ActionCard({ action, index, onUpdate, onRemove, variant = 'card' }) {
  const inspector = variant === 'inspector';
  const { axiosInstance } = useAuth();
  const { state } = useAutomationContext();
  const [expandedState, setExpanded] = useState(!action.type);
  const expanded = inspector || expandedState;
  const [availableFields, setAvailableFields] = useState([]);
  const [configuredWebhooks, setConfiguredWebhooks] = useState([]);
  const [loadingWebhooks, setLoadingWebhooks] = useState(false);
  const [loadingFields, setLoadingFields] = useState(false);
  const [relatedObjects, setRelatedObjects] = useState([]);
  const [createRecordFields, setCreateRecordFields] = useState([]);
  const [selectedCreateFields, setSelectedCreateFields] = useState({});
  const [relatedRecordFields, setRelatedRecordFields] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [loadingEmailTemplates, setLoadingEmailTemplates] = useState(false);
  const [emailTemplateError, setEmailTemplateError] = useState(null);
  const [templatesFetched, setTemplatesFetched] = useState(false);
  const [templatePreview, setTemplatePreview] = useState(null);
  const [openTemplatePreview, setOpenTemplatePreview] = useState(false);
  const [openEmailPreview, setOpenEmailPreview] = useState(false);
  const [signature, setSignature] = useState('');
  const [loadingSignature, setLoadingSignature] = useState(false);
  const [signatureFetched, setSignatureFetched] = useState(false);
  const [selectedFieldToken, setSelectedFieldToken] = useState(null);
  const metadataService = new MetadataService({ axiosInstance });

  const dueDateAnchors = useMemo(() => {
    const anchors = [{ id: 'trigger', label: 'When automation runs' }];
    const objectType = (state.wizardData?.object || '').toLowerCase();
    if (objectType === 'travel_orders' || objectType === 'travel_order') {
      anchors.push({ id: 'departure_date', label: 'Departure date' });
    }
    return anchors;
  }, [state.wizardData?.object]);

  const minDueDateLocal = useMemo(() => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  }, []);

  // Add cleanup effect
  useEffect(() => {
    return () => {
      // Clear cache for this object when component unmounts
      if (state.wizardData.object) {
        metadataService.clearCacheForObject(state.wizardData.object);
      }
    };
  }, []);

  // Load available fields when object changes
  useEffect(() => {
    if (state.wizardData.object) {
      loadAvailableFields();
      loadRelatedObjects();
    }
  }, [state.wizardData.object]);

  // Load webhooks when action type changes to webhook
  useEffect(() => {
    if (action.type === 'Webhook') {
      loadWebhooks();
    }
  }, [action.type]);

  // Load fields for Create Record
  useEffect(() => {
    if (action.type === 'Create Record' && action.parameters?.recordType) {
      loadCreateRecordFields(action.parameters.recordType);
    }
  }, [action.type, action.parameters?.recordType]);

  // Load fields for Update Related Record
  useEffect(() => {
    if (action.type === 'Update Related Record' && action.parameters?.relatedRecordType) {
      loadRelatedRecordFields(action.parameters.relatedRecordType);
    }
  }, [action.type, action.parameters?.relatedRecordType]);

  // Fetch email templates when Send Email action is in use
  useEffect(() => {
    const fetchEmailTemplates = async () => {
      setLoadingEmailTemplates(true);
      setEmailTemplateError(null);
      try {
        const response = await axiosInstance.get('/templates/email');
        setEmailTemplates(response.data || []);
      } catch (error) {
        console.error('Error loading email templates:', error);
        setEmailTemplateError('Unable to load email templates');
        setEmailTemplates([]);
      } finally {
        setLoadingEmailTemplates(false);
        setTemplatesFetched(true);
      }
    };

    if (action.type === 'Send Email' && !templatesFetched && !loadingEmailTemplates) {
      fetchEmailTemplates();
    }
  }, [action.type, axiosInstance, loadingEmailTemplates, templatesFetched]);

  // Fetch saved signature once for email previews
  useEffect(() => {
    const fetchSignature = async () => {
      setLoadingSignature(true);
      try {
        const response = await axiosInstance.get('/email-settings/signature');
        setSignature(response.data?.signature || '');
      } catch (error) {
        console.error('Error loading email signature:', error);
        setSignature('');
      } finally {
        setLoadingSignature(false);
        setSignatureFetched(true);
      }
    };

    if (action.type === 'Send Email' && !signatureFetched && !loadingSignature) {
      fetchSignature();
    }
  }, [action.type, axiosInstance, loadingSignature, signatureFetched]);

    const loadAvailableFields = async () => {
    if (!state.wizardData.object) return;
    
    setLoadingFields(true);
    try {
      const fields = await metadataService.getFields(state.wizardData.object);
      setAvailableFields(fields);
    } catch (error) {
      console.error('Error loading fields:', error);
    } finally {
      setLoadingFields(false);
    }
  };

  const loadRelatedObjects = async () => {
    if (!state.wizardData.object) return;
    
    try {
      const relatedObjects = await metadataService.getRelationships(state.wizardData.object);
      setRelatedObjects(relatedObjects || []);
    } catch (error) {
      console.error('Error loading related objects:', error);
      setRelatedObjects([]);
    }
  };

  const loadCreateRecordFields = async (recordType) => {
    if (!recordType) return;
    
    try {
      const fields = await metadataService.getFields(recordType);
      const filteredFields = fields.filter(field => !field.metadata?.read_only);
      setCreateRecordFields(filteredFields);
      
      // Initialize selected fields for required fields
      const requiredFields = {};
      filteredFields.forEach(field => {
        if (field.required) {
          requiredFields[field.id] = true;
        }
      });
      setSelectedCreateFields(requiredFields);
    } catch (error) {
      console.error('Error loading create record fields:', error);
      setCreateRecordFields([]);
    }
  };

  const loadRelatedRecordFields = async (recordType) => {
    if (!recordType) return;
    
    try {
      const fields = await metadataService.getFields(recordType);
      const filteredFields = fields.filter(field => !field.metadata?.read_only);
      setRelatedRecordFields(filteredFields);
    } catch (error) {
      console.error('Error loading related record fields:', error);
      setRelatedRecordFields([]);
    }
  };

  const loadWebhooks = async () => {
    setLoadingWebhooks(true);
    try {
      // Note: webhook/configs endpoint not in MetadataService, using direct axios
      const response = await axiosInstance.get('/webhook/configs');
      const webhooks = response.data.filter(w => w.direction === 'outbound' && w.is_active);
      setConfiguredWebhooks(webhooks);
    } catch (error) {
      console.error('Error loading webhooks:', error);
      setConfiguredWebhooks([]);
    } finally {
      setLoadingWebhooks(false);
    }
  };

  const testWebhook = async () => {
    try {
      const fieldMapping = action.parameters?.fieldMapping || {};
      const customParams = {};

      if (action.parameters?.customParams) {
        action.parameters.customParams.forEach(param => {
          if (param.key) {
            customParams[param.key] = param.value;
          }
        });
      }

      // Using axiosInstance instead of axios
      const response = await axiosInstance.get('/webhook/test', {
        params: {
          url: action.parameters?.url,
          method: action.parameters?.method || 'GET',
          field_mapping: JSON.stringify(fieldMapping),
          custom_params: JSON.stringify(customParams)
        }
      });

      if (response.data.success) {
        alert('Webhook test successful!');
      } else {
        alert(`Webhook test failed: ${response.data.response_text}`);
      }
    } catch (error) {
      alert(`Webhook test failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleActionTypeChange = (value) => {
    const defaultParameters = value === 'Send Email' ? { includeSignature: true } : {};
    onUpdate({ 
      type: value,
      parameters: defaultParameters,
      timeDelay: action.timeDelay || {
        enabled: false,
        days: 0,
        hours: 0,
        minutes: 0
      }
    });
  };

  const handleTimeDelayToggle = (checked) => {
    onUpdate({
      timeDelay: {
        ...action.timeDelay,
        enabled: checked
      }
    });
  };

  const handleTimeDelayChange = (field, value) => {
    onUpdate({
      timeDelay: {
        ...action.timeDelay,
        [field]: parseInt(value) || 0
      }
    });
  };

  const prettify = (str) => metadataService.formatLabel(str);

  const getActionIcon = () => {
    const actionType = actionTypes.find(t => t.value === action.type);
    return actionType?.icon || <PlayArrowOutlined />;
  };

  // Filter action types based on trigger
  const getFilteredActionTypes = () => {
    return actionTypes.filter(actionType => {
      if (state.wizardData.trigger === 'delete' && actionType.value === 'Update Field') {
        return false;
      }
      if (actionType.value === 'Update Related Record') {
        return state.wizardData.trigger === 'update' || state.wizardData.trigger === 'delete';
      }
      return true;
    });
  };

  const emailFieldOptions = useMemo(() => {
    const baseFields = [
      { label: 'Email (default)', value: '{{email}}' }
    ];

    const dynamicFields = (availableFields || [])
      .filter(field => field.type === 'email' || (field.id || '').toLowerCase().includes('email'))
      .map(field => ({
        label: `${field.label} (${field.id})`,
        value: `{{${field.id}}}`
      }));

    const unique = new Map();
    [...baseFields, ...dynamicFields].forEach(option => {
      if (option.value && !unique.has(option.value)) {
        unique.set(option.value, option);
      }
    });

    return Array.from(unique.values());
  }, [availableFields]);

  const contentFieldOptions = useMemo(() => {
    const auditFields = [
      { label: 'Created Date', value: '{{created_at}}' },
      { label: 'Updated Date', value: '{{updated_at}}' },
      { label: 'Created By', value: '{{created_by_name}}' },
      { label: 'Updated By', value: '{{updated_by_name}}' }
    ];

    const dynamicFields = (availableFields || []).map(field => ({
      label: field.label,
      value: `{{${field.id}}}`
    }));

    const unique = new Map();
    [...auditFields, ...dynamicFields].forEach(option => {
      if (option.value && !unique.has(option.value)) {
        unique.set(option.value, option);
      }
    });

    return Array.from(unique.values());
  }, [availableFields]);

  const emailFilterOptions = useMemo(
    () =>
      createFilterOptions({
        stringify: (option) => (typeof option === 'string' ? option : `${option.label} ${option.value}`)
      }),
    []
  );

  const parseEmailList = useCallback((value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    return value
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);
  }, []);

  const handleEmailListChange = useCallback((fieldKey, newValue) => {
    const normalized = newValue
      .map(item => (typeof item === 'string' ? item : item?.value || item?.label || ''))
      .filter(Boolean);
    const uniqueValues = Array.from(new Set(normalized));

    onUpdate({
      parameters: {
        ...action.parameters,
        [fieldKey]: uniqueValues.join(', ')
      }
    });
  }, [action.parameters, onUpdate]);

  const handleTemplateSelection = useCallback((templateId) => {
    const selectedTemplate = emailTemplates.find(t => t.id === templateId);

    onUpdate({
      parameters: {
        ...action.parameters,
        template: templateId,
        content: selectedTemplate?.content ?? action.parameters?.content ?? '',
        subject: selectedTemplate?.subject || action.parameters?.subject || ''
      }
    });

    setTemplatePreview(selectedTemplate || null);
  }, [action.parameters, emailTemplates, onUpdate]);

  const handleInsertFieldToken = useCallback((token) => {
    if (!token) return;
    const currentContent = action.parameters?.content || '';
    const spacedContent = currentContent ? `${currentContent} ${token}` : token;

    onUpdate({
      parameters: {
        ...action.parameters,
        content: spacedContent
      }
    });
    setSelectedFieldToken(null);
  }, [action.parameters, onUpdate]);

  const formatEmailPreview = useCallback((content) => {
    if (!content) return '';
    const normalized = content.replace(/\n/g, '<br>');
    return `
      <div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; font-size: 14px;">
        ${normalized}
      </div>
    `;
  }, []);

  const buildEmailPreviewHtml = useCallback(() => {
    const includeSignature = action.parameters?.includeSignature !== false;
    const baseContent = action.parameters?.content || '';
    const withSignature = includeSignature && signature ? `${baseContent}\n\n${signature}` : baseContent;
    return formatEmailPreview(withSignature);
  }, [action.parameters, formatEmailPreview, signature]);

  const getActionError = useCallback(
    (field) => state.errors?.[`action_${index}_${field}`],
    [state.errors, index]
  );

  const selectedTemplate = useMemo(
    () => emailTemplates.find(t => t.id === action.parameters?.template),
    [action.parameters?.template, emailTemplates]
  );

  const renderActionConfiguration = () => {
    switch (action.type) {
    case 'Update Field':
      return (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Field to Update</InputLabel>
              <Select
                value={action.parameters?.field || ''}
                onChange={(e) => onUpdate({ 
                  parameters: { ...action.parameters, field: e.target.value, value: '' }
                })}
                label="Field to Update"
                sx={selectSx}
              >
                <MenuItem value="">
                  <em>Select field</em>
                </MenuItem>
                {availableFields.map(field => (
                  <MenuItem key={field.id} value={field.id}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {field.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '13px' }}>
                        {field.type}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            {(() => {
              const selectedField = availableFields.find(f => f.id === action.parameters?.field);
              if (selectedField?.type === 'picklist') {
                return (
                  <FormControl fullWidth size="small">
                    <InputLabel>New Value</InputLabel>
                    <Select
                      value={action.parameters?.value || ''}
                      onChange={(e) => onUpdate({ 
                        parameters: { ...action.parameters, value: e.target.value }
                      })}
                      label="New Value"
                      sx={selectSx}
                    >
                      <MenuItem value="">
                        <em>Select value</em>
                      </MenuItem>
                      {(selectedField.options || []).map(option => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                );
              } else if (selectedField?.metadata?.reference_to) {
                // Handle reference fields - you might want to add a ReferenceAutocomplete component here
                return (
                  <TextField
                    fullWidth
                    size="small"
                    label="New Value"
                    value={action.parameters?.value || ''}
                    onChange={(e) => onUpdate({ 
                      parameters: { ...action.parameters, value: e.target.value }
                    })}
                    placeholder={`Select ${selectedField.label}`}
                    sx={inputSx}
                  />
                );
              } else {
                return (
                  <TextField
                    fullWidth
                    size="small"
                    label="New Value"
                    value={action.parameters?.value || ''}
                    onChange={(e) => onUpdate({ 
                      parameters: { ...action.parameters, value: e.target.value }
                    })}
                    type={selectedField?.type === 'number' || selectedField?.type === 'currency' ? 'number' : 'text'}
                    sx={inputSx}
                  />
                );
              }
            })()}
          </Grid>
        </Grid>
      );

      case 'Create Record':
        return (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Record Type</InputLabel>
                  <Select
                    value={action.parameters?.recordType || ''}
                    onChange={(e) => onUpdate({ 
                      parameters: { ...action.parameters, recordType: e.target.value }
                    })}
                    label="Record Type"
                    sx={selectSx}
                  >
                    <MenuItem value="">
                      <em>Select record type</em>
                    </MenuItem>
                    {relatedObjects.map(obj => (
                      <MenuItem key={obj} value={obj}>
                        {metadataService.formatLabel(obj)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {action.parameters?.recordType && createRecordFields.length > 0 && (
              <Grid container spacing={2}>
                {createRecordFields.map((field) => {
                  const isRequired = field.required;
                  const isChecked = isRequired || !!selectedCreateFields[field.id];
                  const isAutoGenerated = field.metadata?.auto_generated;
                  
                  return (
                    <Grid item xs={12} md={6} key={field.id}>
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        gap: 1,
                        p: 2,
                        border: '1px solid',
                        borderColor: 'outline.variant',
                        borderRadius: 1,
                        bgcolor: 'surface.main'
                      }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isChecked}
                              disabled={isRequired}
                              onChange={e => {
                                if (!isRequired) {
                                  setSelectedCreateFields(prev => ({
                                    ...prev,
                                    [field.id]: e.target.checked
                                  }));
                                  if (!e.target.checked) {
                                    onUpdate({
                                      parameters: {
                                        ...action.parameters,
                                        [field.id]: undefined
                                      }
                                    });
                                  }
                                }
                              }}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {field.label}
                              </Typography>
                              {isRequired && (
                                <Box component="span" sx={(theme) => ({ color: dangerText(theme), ml: 0.5 })}>*</Box>
                              )}
                              {isAutoGenerated && (
                                <Chip
                                  label="Auto-generated"
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                  sx={{ ml: 1, height: '22px', fontSize: FONT.caption }}
                                />
                              )}
                            </Box>
                          }
                        />
                        
                        {isChecked && !isAutoGenerated && (
                          <Box sx={{ width: '100%' }}>
                            {field.type === 'picklist' ? (
                              <FormControl fullWidth size="small">
                                <Select
                                  value={action.parameters?.[field.id] || field.metadata?.default || ''}
                                  onChange={e => onUpdate({
                                    parameters: {
                                      ...action.parameters,
                                      [field.id]: e.target.value
                                    }
                                  })}

                                >
                                  <MenuItem value="">
                                    <em>Select {field.label}</em>
                                  </MenuItem>
                                  {(field.options || []).map(option => (
                                    <MenuItem key={option} value={option}>
                                      {option}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            ) : field.metadata?.reference_to ? (
                              // Add reference field handling
                              <TextField
                                fullWidth
                                size="small"
                                value={action.parameters?.[field.id] || ''}
                                onChange={e => onUpdate({
                                  parameters: {
                                    ...action.parameters,
                                    [field.id]: e.target.value
                                  }
                                })}
                                placeholder={`Select ${field.label}`}
                                sx={{ '& .MuiOutlinedInput-root': { height: '40px' } }}
                              />
                            ) : (
                              <TextField
                                fullWidth
                                size="small"
                                type={field.type === 'number' || field.type === 'currency' ? 'number' : 'text'}
                                value={action.parameters?.[field.id] || ''}
                                onChange={e => onUpdate({
                                  parameters: {
                                    ...action.parameters,
                                    [field.id]: e.target.value
                                  }
                                })}
                                placeholder={`Enter ${field.label.toLowerCase()}`}
                                InputProps={{
                                  readOnly: isAutoGenerated
                                }}
                                sx={{ '& .MuiOutlinedInput-root': { height: '40px' } }}
                              />
                            )}
                          </Box>
                        )}
                        
                        {isAutoGenerated && isChecked && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '13px' }}>
                            This field will be automatically generated when the automation runs
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </>
        );

      case 'Create Task':
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Task Type</InputLabel>
                <Select
                  value={action.parameters?.type || 'Task'}
                  onChange={e => onUpdate({ 
                    parameters: { ...action.parameters, type: e.target.value }
                  })}
                  label="Task Type"
                  sx={selectSx}
                >
                  <MenuItem value="Task">Task</MenuItem>
                  <MenuItem value="Email">Email</MenuItem>
                  <MenuItem value="Call">Call</MenuItem>
                  <MenuItem value="Meeting">Meeting</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                size="small"
                label="Title"
                value={action.parameters?.subject || ''}
                onChange={e => onUpdate({ 
                  parameters: { ...action.parameters, subject: e.target.value }
                })}
                error={!!getActionError('subject')}
                helperText={getActionError('subject')}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Description"
                value={action.parameters?.notes || ''}
                onChange={e => onUpdate({ 
                  parameters: { ...action.parameters, notes: e.target.value }
                })}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DateField
                field={{ id: 'due_date', label: 'Due Date', type: 'datetime' }}
                value={action.parameters?.due_date || ''}
                onChange={(value) =>
                  onUpdate({
                    parameters: { ...action.parameters, due_date: value },
                  })
                }
                anchors={dueDateAnchors}
                minAbsolute={minDueDateLocal}
                error={!!getActionError('due_date')}
                helperText={getActionError('due_date')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Priority</InputLabel>
                <Select
                  value={action.parameters?.priority || 'Medium'}
                  onChange={e => onUpdate({ 
                    parameters: { ...action.parameters, priority: e.target.value }
                  })}
                  label="Priority"
                  sx={selectSx}
                >
                  <MenuItem value="High">
                    <Chip label="High" color="error" size="small" sx={{ mr: 1 }} />
                    High Priority
                  </MenuItem>
                  <MenuItem value="Medium">
                    <Chip label="Medium" color="warning" size="small" sx={{ mr: 1 }} />
                    Medium Priority
                  </MenuItem>
                  <MenuItem value="Low">
                    <Chip label="Low" color="success" size="small" sx={{ mr: 1 }} />
                    Low Priority
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        );

      case 'Send Email': {
        const includeSignature = action.parameters?.includeSignature !== false;
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Email Template</InputLabel>
                <Select
                  value={action.parameters?.template || ''}
                  onChange={(e) => handleTemplateSelection(e.target.value)}
                  label="Email Template"
                  sx={selectSx}
                  error={!!emailTemplateError}
                >
                  <MenuItem value="">
                    <em>Select template</em>
                  </MenuItem>
                  {loadingEmailTemplates && (
                    <MenuItem disabled>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      Loading templates...
                    </MenuItem>
                  )}
                  {!loadingEmailTemplates && emailTemplates.length === 0 && (
                    <MenuItem disabled>No email templates available</MenuItem>
                  )}
                  {emailTemplates.map(template => (
                    <MenuItem key={template.id} value={template.id}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" fontWeight="medium">
                          {template.name}
                        </Typography>
                        {template.format && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '13px' }}>
                            {template.format.toUpperCase()}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100%' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={includeSignature}
                      onChange={(e) => onUpdate({
                        parameters: {
                          ...action.parameters,
                          includeSignature: e.target.checked
                        }
                      })}
                    />
                  }
                  label="Include Signature"
                />
                <Tooltip title="Preview selected template">
                  <span>
                    <MDButton
                      variant="outlined"
                      color="info"
                      size="small"
                      onClick={() => {
                        setTemplatePreview(selectedTemplate || null);
                        setOpenTemplatePreview(true);
                      }}
                      disabled={!selectedTemplate}
                      startIcon={<Visibility fontSize="small" />}
                    >
                      Preview Template
                    </MDButton>
                  </span>
                </Tooltip>
              </Box>
              {loadingSignature && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '13px' }}>
                  Loading signature...
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                multiple
                freeSolo
                options={emailFieldOptions}
                value={parseEmailList(action.parameters?.toEmail)}
                onChange={(_, newValue) => handleEmailListChange('toEmail', newValue)}
                filterOptions={(options, params) => {
                  const filtered = emailFilterOptions(options, params);
                  if (params.inputValue !== '') {
                    const exists = options.some(opt => opt.value === params.inputValue);
                    if (!exists) {
                      filtered.push({ label: params.inputValue, value: params.inputValue });
                    }
                  }
                  return filtered;
                }}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.label || option.value)}
                isOptionEqualToValue={(option, value) =>
                  (typeof option === 'string' ? option : option.value) === (typeof value === 'string' ? value : value?.value)
                }
                renderTags={(value, getTagProps) =>
                  value.map((option, tagIndex) => {
                    const label = typeof option === 'string' ? option : option.value;
                    return <Chip {...getTagProps({ index: tagIndex })} label={label} size="small" />;
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="To"
                    placeholder="Type or select recipients"
                    error={!!getActionError('toEmail')}
                    helperText={getActionError('toEmail') || 'Use email addresses or {{field}} placeholders'}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                multiple
                freeSolo
                options={emailFieldOptions}
                value={parseEmailList(action.parameters?.ccEmail)}
                onChange={(_, newValue) => handleEmailListChange('ccEmail', newValue)}
                filterOptions={(options, params) => {
                  const filtered = emailFilterOptions(options, params);
                  if (params.inputValue !== '') {
                    const exists = options.some(opt => opt.value === params.inputValue);
                    if (!exists) {
                      filtered.push({ label: params.inputValue, value: params.inputValue });
                    }
                  }
                  return filtered;
                }}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.label || option.value)}
                isOptionEqualToValue={(option, value) =>
                  (typeof option === 'string' ? option : option.value) === (typeof value === 'string' ? value : value?.value)
                }
                renderTags={(value, getTagProps) =>
                  value.map((option, tagIndex) => {
                    const label = typeof option === 'string' ? option : option.value;
                    return <Chip {...getTagProps({ index: tagIndex })} label={label} size="small" />;
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="CC (optional)"
                    placeholder="Add CC recipients"
                    helperText="Optional: add CC addresses or email fields"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Subject"
                value={action.parameters?.subject || ''}
                onChange={(e) => onUpdate({
                  parameters: { ...action.parameters, subject: e.target.value }
                })}
                placeholder="Email subject"
                error={!!getActionError('subject')}
                helperText={getActionError('subject')}
                sx={inputSx}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                value={selectedFieldToken}
                onChange={(_, newValue) => {
                  setSelectedFieldToken(newValue);
                  handleInsertFieldToken(typeof newValue === 'string' ? newValue : newValue?.value);
                }}
                options={contentFieldOptions}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.label || option.value)}
                isOptionEqualToValue={(option, value) =>
                  (typeof option === 'string' ? option : option.value) === (typeof value === 'string' ? value : value?.value)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Insert Field"
                    placeholder="Select a field to insert"
                  />
                )}
                clearOnBlur
                sx={{ width: '100%' }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '13px' }}>
                {'Inserts the selected {{field}} placeholder into the email content.'}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={8}
                label="Email Content"
                value={action.parameters?.content || ''}
                onChange={(e) => onUpdate({
                  parameters: {
                    ...action.parameters,
                    content: e.target.value,
                  }
                })}
                placeholder="Write your email content here... Use {{field_name}} to include dynamic data."
                error={!!getActionError('content')}
                helperText={
                  getActionError('content') ||
                  (action.parameters?.template
                    ? 'Template content loaded. You can modify it and add dynamic fields.'
                    : 'Write your email content or select a template above.')
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <MDButton
                  variant="gradient"
                  color="info"
                  size="small"
                  startIcon={<Visibility />}
                  onClick={() => setOpenEmailPreview(true)}
                  disabled={!action.parameters?.content}
                >
                  Preview Email
                </MDButton>
              </Box>
            </Grid>
          </Grid>
        );
      }

      case 'Update Related Record':
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Related Record Type</InputLabel>
                <Select
                  value={action.parameters?.relatedRecordType || ''}
                  onChange={(e) => onUpdate({ 
                    parameters: { 
                      ...action.parameters, 
                      relatedRecordType: e.target.value, 
                      relatedField: '', 
                      relatedValue: '' 
                    }
                  })}
                  label="Related Record Type"
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em>Select type</em>
                  </MenuItem>
                  {relatedObjects.map(obj => (
                    <MenuItem key={obj} value={obj}>
                      {metadataService.formatLabel(obj)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Field to Update</InputLabel>
                <Select
                  value={action.parameters?.relatedField || ''}
                  onChange={(e) => onUpdate({ 
                    parameters: { ...action.parameters, relatedField: e.target.value }
                  })}
                  label="Field to Update"
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em>Select field</em>
                  </MenuItem>
                  {relatedRecordFields.map(field => (
                    <MenuItem key={field.id} value={field.id}>
                      {field.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              {(() => {
                const selectedField = relatedRecordFields.find(f => f.id === action.parameters?.relatedField);
                if (selectedField?.type === 'picklist') {
                  return (
                    <FormControl fullWidth size="small">
                      <InputLabel>New Value</InputLabel>
                      <Select
                        value={action.parameters?.relatedValue || ''}
                        onChange={(e) => onUpdate({ 
                          parameters: { ...action.parameters, relatedValue: e.target.value }
                        })}
                        label="New Value"
                        sx={selectSx}
                      >
                        <MenuItem value=""><em>Select value</em></MenuItem>
                        {(selectedField.options || []).map(option => (
                          <MenuItem key={option} value={option}>{option}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  );
                } else {
                  return (
                    <TextField
                      fullWidth
                      size="small"
                      label="New Value"
                      value={action.parameters?.relatedValue || ''}
                      onChange={(e) => onUpdate({ 
                        parameters: { ...action.parameters, relatedValue: e.target.value }
                      })}
                      sx={inputSx}
                    />
                  );
                }
              })()}
            </Grid>
          </Grid>
        );

      case 'Webhook':
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Select Webhook</InputLabel>
                <Select
                  value={action.parameters?.webhookId || ''}
                  onChange={(e) => {
                    const selectedWebhook = configuredWebhooks.find(w => w.id === e.target.value);
                    onUpdate({
                      parameters: {
                        ...action.parameters,
                        webhookId: e.target.value,
                        url: selectedWebhook?.url || '',
                        method: selectedWebhook?.method || 'GET',
                        name: selectedWebhook?.name || ''
                      }
                    });
                  }}
                  label="Select Webhook"
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em>Select a configured webhook</em>
                  </MenuItem>
                  {loadingWebhooks ? (
                    <MenuItem disabled>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      Loading webhooks...
                    </MenuItem>
                  ) : configuredWebhooks.length === 0 ? (
                    <MenuItem disabled>
                      No webhooks configured
                    </MenuItem>
                  ) : (
                    configuredWebhooks.map(webhook => (
                      <MenuItem key={webhook.id} value={webhook.id}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2" fontWeight="medium">
                            {webhook.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '13px' }}>
                            {webhook.url} ({webhook.method})
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Webhook URL"
                value={action.parameters?.url || ''}
                InputProps={{ readOnly: true }}
                sx={inputSx}
              />
            </Grid>
            
            {action.parameters?.webhookId && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Field Mapping
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Select which fields from the {state.wizardData.object} record to send to the webhook.
                  </Typography>
                  <Grid container spacing={1}>
                    {availableFields.map(field => (
                      <Grid item xs={12} md={6} key={field.id}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!action.parameters?.fieldMapping?.[field.id]}
                              onChange={(e) => {
                                const fieldMapping = action.parameters?.fieldMapping || {};
                                if (e.target.checked) {
                                  fieldMapping[field.id] = field.id;
                                } else {
                                  delete fieldMapping[field.id];
                                }
                                onUpdate({
                                  parameters: {
                                    ...action.parameters,
                                    fieldMapping
                                  }
                                });
                              }}
                            />
                          }
                          label={
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body2">
                                  {field.label}
                                </Typography>
                                {field.metadata?.is_custom && (
                                  <Chip
                                    label="Custom"
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ ml: 1, height: '22px', fontSize: FONT.caption }}
                                  />
                                )}
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '13px' }}>
                                {field.type}
                              </Typography>
                            </Box>
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Custom Parameters (Optional)
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    {action.parameters?.customParams?.map((param, paramIndex) => (
                      <Box key={paramIndex} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                        <TextField
                          size="small"
                          label="Parameter Name"
                          value={param.key || ''}
                          onChange={(e) => {
                            const newParams = [...(action.parameters?.customParams || [])];
                            newParams[paramIndex] = { ...param, key: e.target.value };
                            onUpdate({ 
                              parameters: { ...action.parameters, customParams: newParams }
                            });
                          }}
                          sx={{ flex: 1, '& .MuiInputBase-root': { height: '40px' } }}
                        />
                        <TextField
                          size="small"
                          label="Parameter Value"
                          value={param.value || ''}
                          onChange={(e) => {
                            const newParams = [...(action.parameters?.customParams || [])];
                            newParams[paramIndex] = { ...param, value: e.target.value };
                            onUpdate({ 
                              parameters: { ...action.parameters, customParams: newParams }
                            });
                          }}
                          sx={{ flex: 1, '& .MuiInputBase-root': { height: '40px' } }}
                        />
                        <IconButton
                          aria-label="Remove parameter"
                          sx={tintedIconButtonSx('error')}
                          onClick={() => {
                            const newParams = action.parameters?.customParams?.filter((_, i) => i !== paramIndex) || [];
                            onUpdate({ 
                              parameters: { ...action.parameters, customParams: newParams }
                            });
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    ))}
                    <MDButton
                      variant="gradient"
                      color="info"
                      size="small"
                      startIcon={<Add />}
                      onClick={() => {
                        const newParams = [...(action.parameters?.customParams || []), { key: '', value: '' }];
                        onUpdate({ 
                          parameters: { ...action.parameters, customParams: newParams }
                        });
                      }}
                    >
                      Add Custom Parameter
                    </MDButton>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <MDButton
                      variant="gradient"
                      color="success"
                      size="small"
                      onClick={testWebhook}
                      disabled={!action.parameters?.url}
                    >
                      Test Webhook
                    </MDButton>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '13px' }}>
                      Test the webhook configuration before saving
                    </Typography>
                  </Box>
                </Grid>
              </>
            )}

            {configuredWebhooks.length === 0 && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  <Typography variant="body2">
                    No webhooks configured. Please configure webhooks in{' '}
                    <strong>Settings → Integrations → Webhook Integrations</strong> first.
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        );

      default:
        return null;
    }
  };

  const activeTemplate = templatePreview || selectedTemplate;

  return (
    <>
      <Card
        elevation={0}
        sx={(theme) => ({
          borderRadius: '12px',
          border: '1px solid',
          borderColor: action.type ? theme.palette.outline.variant : statusColors(theme, 'warning').border,
          bgcolor: 'background.paper',
          backgroundImage: 'none',
          boxShadow: 'none',
          transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
          '&:hover': {
            borderColor: action.type ? 'primary.main' : statusColors(theme, 'warning').border,
            boxShadow: 2
          }
        })}
      >
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            {!inspector && (
            <Box sx={action.type ? badgeSx(36) : mutedBadgeSx(36)}>
              {index + 1}
            </Box>
            )}

            <FormControl fullWidth size="small">
              <InputLabel>
                <Box display="flex" alignItems="center" gap={0.5}>
                  {getActionIcon()}
                  Action Type
                </Box>
              </InputLabel>
              <Select
                value={action.type || ''}
                onChange={(e) => handleActionTypeChange(e.target.value)}
                label="Action Type"
                sx={selectSx}
              >
                <MenuItem value="">
                  <Typography component="span" sx={placeholderSx}>Select an action</Typography>
                </MenuItem>
                {getFilteredActionTypes().map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {React.cloneElement(type.icon, { 
                        fontSize: 'small', 
                        sx: { color: 'primary.main' } 
                      })}
                      {type.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {!inspector && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                onClick={() => setExpanded(!expanded)}
                aria-label={expanded ? 'Collapse action' : 'Expand action'}
                sx={tintedIconButtonSx('primary')}
              >
                {expanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
              <IconButton
                onClick={onRemove}
                aria-label="Remove action"
                sx={tintedIconButtonSx('error')}
              >
                <DeleteOutline />
              </IconButton>
            </Box>
            )}
          </Box>

          <Collapse in={expanded}>
            <Box sx={{ mt: 3 }}>
              {action.type && (
                <>
                  <Box 
                    sx={inspector ? { mb: 1 } : { 
                      mb: 3, 
                      p: 2, 
                      borderRadius: '8px',
                      bgcolor: 'surface.main',
                      border: '1px solid',
                      borderColor: 'outline.variant'
                    }}
                  >
                    {!inspector && (
                    <Typography sx={{ mb: 2, fontWeight: 600, fontSize: FONT.body, color: 'text.primary' }}>
                      Configuration
                    </Typography>
                    )}
                    
                    {renderActionConfiguration()}
                  </Box>

                  {!inspector && (
                  <Box 
                    sx={(theme) => ({ 
                      p: 2.5, 
                      borderRadius: '12px',
                      bgcolor: action.timeDelay?.enabled 
                        ? statusColors(theme, 'warning').bg
                        : 'surface.main',
                      border: '1px solid',
                      borderColor: action.timeDelay?.enabled 
                        ? statusColors(theme, 'warning').border
                        : theme.palette.outline.variant
                    })}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={action.timeDelay?.enabled || false}
                          onChange={(e) => handleTimeDelayToggle(e.target.checked)}
                          color="warning"
                        />
                      }
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <AccessTime 
                            sx={(theme) => ({ 
                              color: action.timeDelay?.enabled ? statusColors(theme, 'warning').text : 'text.secondary',
                              fontSize: 20 
                            })} 
                          />
                          <Typography 
                            sx={{
                              fontSize: FONT.body,
                              fontWeight: 500,
                              color: 'text.primary'
                            }}
                          >
                            Add time delay
                          </Typography>
                        </Box>
                      }
                    />
                    
                    <Collapse in={action.timeDelay?.enabled}>
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Days"
                            value={action.timeDelay?.days || 0}
                            onChange={(e) => handleTimeDelayChange('days', e.target.value)}
                            InputProps={{ inputProps: { min: 0 } }}
                            sx={inputSx}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Hours"
                            value={action.timeDelay?.hours || 0}
                            onChange={(e) => handleTimeDelayChange('hours', e.target.value)}
                            InputProps={{ inputProps: { min: 0, max: 23 } }}
                            sx={inputSx}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Minutes"
                            value={action.timeDelay?.minutes || 0}
                            onChange={(e) => handleTimeDelayChange('minutes', e.target.value)}
                            InputProps={{ inputProps: { min: 0, max: 59 } }}
                            sx={inputSx}
                          />
                        </Grid>
                      </Grid>
                      
                      {action.timeDelay?.enabled && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          <Typography variant="body2">
                            <strong>Total Delay:</strong>{' '}
                            {action.timeDelay?.days > 0 && `${action.timeDelay.days} day(s) `}
                            {action.timeDelay?.hours > 0 && `${action.timeDelay.hours} hour(s) `}
                            {action.timeDelay?.minutes > 0 && `${action.timeDelay.minutes} minute(s)`}
                            {!action.timeDelay?.days && !action.timeDelay?.hours && !action.timeDelay?.minutes && 'No delay set'}
                          </Typography>
                        </Alert>
                      )}
                    </Collapse>
                  </Box>
                  )}
                </>
              )}

              {!action.type && (
                <Box 
                  sx={(theme) => ({ 
                    textAlign: 'center', 
                    py: 3, 
                    px: 2,
                    borderRadius: '8px',
                    bgcolor: statusColors(theme, 'warning').bg,
                    border: '1px dashed',
                    borderColor: statusColors(theme, 'warning').border
                  })}
                >
                  <Typography sx={{ fontSize: FONT.body, color: 'text.primary' }}>
                    Please select an action type to configure this step
                  </Typography>
                </Box>
              )}
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      <Dialog
        open={openTemplatePreview}
        onClose={() => setOpenTemplatePreview(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Template Preview</DialogTitle>
        <DialogContent dividers>
          {activeTemplate ? (
            activeTemplate.format === 'html' ? (
              <Box
                sx={emailCanvasSx}
                dangerouslySetInnerHTML={{ __html: activeTemplate.content || '' }}
              />
            ) : (
              <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', ...monoFontSx }}>
                {activeTemplate.content || ''}
              </Typography>
            )
          ) : (
            <Typography variant="body2" color="text.secondary">
              Select a template to preview its content.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTemplatePreview(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openEmailPreview}
        onClose={() => setOpenEmailPreview(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Email Preview</DialogTitle>
        <DialogContent dividers>
          {action.parameters?.content ? (
            <Box
              sx={emailCanvasSx}
              dangerouslySetInnerHTML={{ __html: buildEmailPreviewHtml() }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Add content to preview the email.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEmailPreview(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ActionCard;
